import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchProjectSearch, fetchProject, fetchManagers } from "../api";
import {
  PageHeader, Card, Stat, Loading, ErrorState, EmptyState, SearchBox, Select,
} from "../components/ui";
import { money0, hrs, num, fmtPeriod } from "../lib/format";
import { readUi, writeUi } from "../lib/persist";

/**
 * One project's economics.
 *
 * Three sources have to agree for a margin to mean anything: revenue and
 * coded costs (both from the ledger) and the hours people put in. They are
 * imported separately and can cover different periods, so the page states the
 * span of each rather than quietly subtracting one from another. Where they
 * do not overlap it says so, because a margin built from a full year of
 * revenue and two months of hours is worse than no margin at all.
 *
 * Labour is costed at a rate fd maintains, never at the ledger's own
 * cost rate - Deltek's own, shown alongside for comparison.
 */
const overlaps = (a, b) => {
  const key = (p) => (p ? `${p.split("/")[1]}${p.split("/")[0].padStart(2, "0")}` : null);
  if (!a?.first || !b?.first) return null;
  return key(a.first) <= key(b.last) && key(b.first) <= key(a.last);
};

const Span = ({ label, span }) => (
  <span className="muted">
    {label}: {span?.first ? `${fmtPeriod(span.first)} – ${fmtPeriod(span.last)}` : "none"}
  </span>
);

export default function Project({ username }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  /*
   * A partner only ever sees projects they had a hand in - the server enforces
   * it, this is just the picker. fd sees everything and can narrow to one
   * partner to see exactly what that partner sees.
   */
  const isFd = String(username || "").toLowerCase() === "fd";
  const [partner, setPartner] = useState("All");
  const [managers, setManagers] = useState([]);
  useEffect(() => {
    if (isFd) fetchManagers().then(setManagers).catch(() => {});
  }, [isFd]);
  const asPartner = isFd && partner !== "All" ? partner : null;
  const [code, setCode] = useState(() => readUi("proj.code", ""));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchProjectSearch(q, asPartner).then((r) => setList(r.rows)).catch(() => {});
  }, [q, asPartner]);

  const load = useCallback(() => {
    if (!code) { setData(null); return; }
    setLoading(true); setError("");
    fetchProject(code, asPartner)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [code, asPartner]);
  useEffect(load, [load]);
  useEffect(() => { writeUi("proj.code", code); }, [code]);
  useEffect(() => { setCode(""); }, [asPartner]);

  const t = data?.totals;
  const cov = data?.coverage;
  // A margin is only stated when the hours and the revenue describe the same
  // stretch of time, and every hour on the project has a rate behind it.
  // Hours are priced from Deltek's own labour cost unless fd has overridden
  // it, so this is now normally true; it stays as a guard for the odd month
  // where a person billed nothing and there is no rate to fall back on.
  const costed = t && t.unratedHours === 0 && t.hours > 0;
  const aligned = cov && overlaps(cov.revenue, cov.labour);
  const margin = t ? t.revenue - t.costs - t.labourCost : 0;

  const options = useMemo(
    () => [{ value: "", label: "Choose a project…" }].concat(
      list.map((p) => ({
        value: p.code,
        label: `${p.code} — ${p.name || "(no name)"}`
          + (num(p.revenue) ? ` · ${money0(p.revenue)}` : "")
          + (num(p.hours) ? ` · ${hrs(p.hours)}h` : ""),
      }))
    ), [list]);

  return (
    <main className="page page--wide">
      <PageHeader
        title="Project"
        subtitle={data ? `${data.project.code} · ${data.project.name || "unnamed"}`
                       : "Revenue, coded costs and labour for a single project"}
      />

      <div className="stack">
        <Card title="Choose a project"
              note={isFd
                ? (asPartner
                    ? "showing only what this partner can see"
                    : "every project with revenue or hours")
                : "your projects — those you sourced, ran, or booked time to"}>
          <div className="toolbar">
            {isFd && (
              <Select value={partner} onChange={setPartner} style={{ minWidth: 190 }}
                      options={[{ value: "All", label: "All partners" }].concat(
                        managers.map((m) => ({
                          value: m.username,
                          label: m.display_name || m.username.toUpperCase(),
                        })))} />
            )}
            <SearchBox value={q} onChange={setQ} placeholder="Search code or name…" width={280} />
            <Select value={code} onChange={setCode} options={options} style={{ minWidth: 420 }} />
          </div>
        </Card>

        {loading && <Loading />}
        {error && <ErrorState message={error} onRetry={load} />}
        {!code && !loading && (
          <Card><EmptyState title="No project selected"
                            hint="Pick one above to see what it earned and what it cost." /></Card>
        )}

        {data && !loading && (
          <>
            {data.project.isOverhead && (
              <Card>
                <p className="toolbar-note">
                  <strong>This is an overhead bucket, not a client project.</strong> Salary
                  and running costs are posted here in bulk, so a margin against it means
                  nothing. It is listed only so the money is not invisible.
                </p>
              </Card>
            )}

            <div className="stat-grid">
              <Stat label="Revenue" value={money0(t.revenue)} accent="var(--s-1)"
                    meta={cov.revenue?.first ? `${fmtPeriod(cov.revenue.first)} – ${fmtPeriod(cov.revenue.last)}` : "none"} />
              <Stat label="Coded costs" value={money0(t.costs)} accent="var(--s-2)"
                    meta="expenses posted to this project" />
              <Stat label="Hours" value={hrs(t.hours)} accent="var(--s-3)"
                    meta={cov.labour?.first ? `${fmtPeriod(cov.labour.first)} – ${fmtPeriod(cov.labour.last)}` : "no hours logged"} />
              <Stat label="Labour cost"
                    value={costed ? money0(t.labourCost) : "—"}
                    accent="var(--s-4)"
                    meta={costed ? "hours priced month by month"
                                 : `${hrs(t.unratedHours)}h could not be priced`} />
            </div>

            {(!costed || aligned === false) && (
              <Card title="Why no margin is shown">
                <ul className="toolbar-note" style={{ margin: 0, paddingLeft: 18 }}>
                  {aligned === false && (
                    <li>
                      Revenue runs {fmtPeriod(cov.revenue.first)}–{fmtPeriod(cov.revenue.last)} but
                      hours run {fmtPeriod(cov.labour.first)}–{fmtPeriod(cov.labour.last)}. The two
                      do not overlap, so subtracting one from the other would compare
                      different periods of work.
                    </li>
                  )}
                  {t.hours === 0 && <li>No hours have been logged against this project.</li>}
                  {t.hours > 0 && t.unratedHours > 0 && (
                    <li>
                      {hrs(t.unratedHours)} of {hrs(t.hours)} hours could not be priced — the
                      report carried no cost amount for those months and no rate has been set
                      for that person. Add one under Admin &gt; Rate card.
                    </li>
                  )}
                </ul>
              </Card>
            )}

            {costed && aligned && (
              <div className="stat-grid">
                <Stat label="Margin" value={money0(margin)} accent="var(--s-1)"
                      meta="revenue less coded costs and labour" />
                <Stat label="Margin %" plain
                      value={t.revenue ? `${((margin / t.revenue) * 100).toFixed(1)}%` : "—"} />
              </div>
            )}

            {!!t.doubleCounted && (
              <Card title="Counted twice?">
                <p className="toolbar-note" style={{ margin: 0 }}>
                  {t.doubleCounted === 1 ? "One person on" : `${t.doubleCounted} people on`} this
                  project {t.doubleCounted === 1 ? "has" : "have"} salary posted
                  <strong> directly to it</strong> as well as hours logged against it — temp staff
                  billed at a day rate. Their cost is already in <em>Coded costs</em> below, so
                  giving them a rate would count the same work twice. Leave their rate unset, or
                  date it to start after the day-rate billing stopped.
                </p>
              </Card>
            )}

            <Card title="Labour"
                note="priced month by month at Deltek's own labour cost, unless a rate has been set to override it"
                flush>
              {!data.labour.length ? (
                <EmptyState title="No hours logged" hint="Nobody has booked time to this project." />
              ) : (
                <div className="tbl-wrap tbl-wrap--flush">
                  <table className="tbl tbl-zebra">
                    <thead>
                      <tr>
                        <th>Person</th>
                        <th className="num">Hours</th>
                        <th className="num">Rate</th>
                        <th className="num">Cost</th>
                        <th className="num muted">Deltek cost</th>
                        <th>Period</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.labour.map((l) => (
                        <tr key={l.employee_code}>
                          <td>
                            {l.employee_name}
                            {l.coded_amount != null && (
                              <span className="pill pill--warn" style={{ marginLeft: 8 }}
                                    title={`${money0(l.coded_amount)} of salary is posted straight to this `
                                         + `project in their name (${fmtPeriod(l.coded_first)}`
                                         + `${l.coded_last !== l.coded_first ? ` – ${fmtPeriod(l.coded_last)}` : ""}). `
                                         + `Costing their hours as well would count the same work twice.`}>
                                already coded
                              </span>
                            )}
                          </td>
                          <td className="num">{hrs(l.hours)}</td>
                          <td className="num">
                            {l.rate == null ? <span className="muted">—</span> : money0(l.rate)}
                            <span className="proj-rate-src">
                              {l.has_override && l.has_ledger ? "yours + Deltek"
                                : l.has_override ? "yours"
                                : l.has_ledger ? "Deltek" : ""}
                            </span>
                          </td>
                          <td className="num">{l.cost == null ? "—" : money0(l.cost)}</td>
                          <td className="num muted">{money0(l.billing)}</td>
                          <td>{fmtPeriod(l.first_period)}
                            {l.last_period !== l.first_period && ` – ${fmtPeriod(l.last_period)}`}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td><strong>Total</strong></td>
                        <td className="num"><strong>{hrs(t.hours)}</strong></td>
                        <td />
                        <td className="num"><strong>{costed ? money0(t.labourCost) : "—"}</strong></td>
                        <td className="num muted">{money0(t.billing)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </Card>

            <Card title="Coded costs" note="expenses posted directly to this project" flush>
              {!data.costs.length ? (
                <EmptyState title="No costs coded" hint="Nothing has been posted to this project." />
              ) : (
                <div className="tbl-wrap tbl-wrap--flush">
                  <table className="tbl tbl-zebra">
                    <thead>
                      <tr>
                        <th>Account</th><th>Name</th>
                        <th className="num">Rows</th><th className="num">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.costs.map((c) => (
                        <tr key={c.account}>
                          <td>{c.account}</td><td>{c.account_name}</td>
                          <td className="num muted">{c.rows}</td>
                          <td className="num">{money0(c.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3}><strong>Total</strong></td>
                        <td className="num"><strong>{money0(t.costs)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </Card>

            <Card title="Revenue" note={`${data.revenue.length} invoice line(s)`} flush>
              <div className="tbl-wrap tbl-wrap--flush">
                <table className="tbl tbl-zebra">
                  <thead>
                    <tr>
                      <th>Period</th><th>Client / invoice</th>
                      <th>Team</th><th>Genesis</th><th className="num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.revenue.map((r, i) => (
                      <tr key={i}>
                        <td>{fmtPeriod(r.period)}</td>
                        <td>{r.description}</td>
                        <td>{(r.username || "").toUpperCase()}</td>
                        <td className="muted">{r.genesis}</td>
                        <td className="num">{money0(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4}><strong>Total</strong></td>
                      <td className="num"><strong>{money0(t.revenue)}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>

            <p className="toolbar-note">
              <Span label="Revenue" span={cov.revenue} /> · <Span label="Costs" span={cov.costs} />
              {" · "}<Span label="Hours" span={cov.labour} />. Labour is never costed at the
              Deltek's own cost amount; the column beside it shows what the report carried.
            </p>
          </>
        )}
      </div>
    </main>
  );
}
